import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Calendar, User } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import NewsletterForm from '@/components/NewsletterForm';
import { fetchContent } from '@/lib/contentApi';

export default function InsightsPage() {
  const [insights, setInsights] = useState(null); // null = loading

  useEffect(() => {
    fetchContent('insights').then((items) => {
      const published = (items || []).filter((i) => i.published === true);
      // newest first if publish_date sortable, otherwise CMS order
      setInsights(published);
    });
  }, []);

  const [featured, ...rest] = insights || [];

  return (
    <>
      <SEOHead
        title="Insights by Ogaba Joseph Attah on Digital Infrastructure Intelligence"
        description="Original writing and analysis on digital infrastructure intelligence, decision-making systems, fibre networks, and African technology ecosystems by Ogaba Joseph Attah."
        canonicalUrl="https://www.ogabajosephattah.com/insights"
      />

      <Navigation />

      <main className="pt-24 lg:pt-32">
        {/* Hero */}
        <section className="py-16 md:py-24" data-testid="insights-hero">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-6">
                Thought Leadership
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[0.95] mb-6" data-testid="insights-title">
                Insights by Ogaba Joseph Attah
              </h1>
              <p className="text-lg text-muted-foreground">
                Original writing and analysis on digital infrastructure intelligence, decision-making systems, fibre networks, scalable connectivity, and African technology ecosystems.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Insight */}
        {insights === null ? (
          <section className="py-12 text-center text-muted-foreground" data-testid="insights-loading">Loading insights…</section>
        ) : insights.length === 0 ? (
          <section className="py-12 md:py-16" data-testid="insights-empty">
            <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
              <div className="inline-flex p-4 bg-primary/10 rounded-2xl mb-6">
                <BookOpen className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4">More insights coming soon</h2>
              <p className="text-muted-foreground mb-8">
                Long-form analysis and founder commentary on digital infrastructure intelligence is in the works. Subscribe to be the first to read.
              </p>
              <div className="max-w-md mx-auto">
                <NewsletterForm variant="inline" />
              </div>
            </div>
          </section>
        ) : (
          <>
            {featured && (
              <section className="py-12 md:py-16" data-testid="featured-insight">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                  >
                    <Link
                      to={`/insights/${featured.slug}`}
                      className="group block overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10 hover:border-primary/40 transition-all duration-300"
                      data-testid="featured-insight-card"
                    >
                      <div className="grid lg:grid-cols-2 gap-0">
                        {featured.cover_image && (
                          <div className="aspect-[4/3] lg:aspect-auto bg-muted overflow-hidden">
                            <img
                              src={featured.cover_image}
                              alt={featured.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          </div>
                        )}
                        <div className="p-8 md:p-12 flex flex-col justify-center">
                          <div className="flex items-center gap-3 mb-5">
                            <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-3 py-1 rounded-full">
                              Featured
                            </span>
                            {featured.publish_date && (
                              <span className="text-sm text-muted-foreground inline-flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5" /> {featured.publish_date}
                              </span>
                            )}
                          </div>
                          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-4 group-hover:text-primary transition-colors">
                            {featured.title}
                          </h2>
                          {featured.excerpt && (
                            <p className="text-muted-foreground text-base md:text-lg mb-6 max-w-2xl">
                              {featured.excerpt}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-primary font-medium">
                            Read Article
                            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                </div>
              </section>
            )}

            {rest.length > 0 && (
              <section className="py-12 md:py-20 bg-muted/30" data-testid="insights-grid">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
                  <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-10">More Articles</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {rest.map((post, index) => (
                      <motion.div
                        key={post.id || post.slug}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: index * 0.08 }}
                        data-testid={`insight-card-${index}`}
                      >
                        <Link
                          to={`/insights/${post.slug}`}
                          className="group block h-full bg-card border border-border rounded-xl overflow-hidden hover:border-primary/40 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                        >
                          {post.cover_image && (
                            <div className="aspect-[16/9] bg-muted overflow-hidden">
                              <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                            </div>
                          )}
                          <div className="p-6">
                            {post.publish_date && (
                              <span className="text-xs text-muted-foreground inline-flex items-center gap-1 mb-3">
                                <Calendar className="h-3 w-3" /> {post.publish_date}
                              </span>
                            )}
                            <h4 className="text-lg font-semibold leading-tight mb-3 group-hover:text-primary transition-colors">
                              {post.title}
                            </h4>
                            {post.excerpt && (
                              <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                            )}
                            {post.author && (
                              <p className="text-xs text-muted-foreground mt-4 inline-flex items-center gap-1">
                                <User className="h-3 w-3" /> {post.author}
                              </p>
                            )}
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* Newsletter CTA */}
        <section className="py-24 md:py-32" data-testid="insights-cta">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Stay informed
            </h2>
            <p className="text-muted-foreground mb-8">
              Subscribe to receive new articles on digital infrastructure intelligence, technology leadership, and emerging market innovation.
            </p>
            <div className="max-w-md mx-auto">
              <NewsletterForm variant="inline" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
